import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import Card from "../../components/Card";
import { 
  ArrowLeft, 
  Home, 
  Bell, 
  Send,
  Users,
  User,
  Mail,
  MessageSquare,
  CheckCircle,
  AlertCircle
} from "lucide-react";

interface NotificationTemplate {
  id: string;
  name: string;
  subject: string;
  message: string;
}

export default function Notifications() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [targetType, setTargetType] = useState<"all" | "instructors" | "students" | "specific">("all");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sendStatus, setSendStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");

  const templates: NotificationTemplate[] = [
    {
      id: "welcome",
      name: "ข้อความต้อนรับ",
      subject: "ยินดีต้อนรับสู่ EduMi!",
      message: "สวัสดีครับ!\n\nขอบคุณที่เข้าร่วมกับ EduMi เราหวังว่าคุณจะมีประสบการณ์การเรียนรู้ที่ดีกับเรา\n\nทีมงาน EduMi"
    },
    {
      id: "subscription_reminder",
      name: "แจ้งเตือน Subscription",
      subject: "แจ้งเตือน: Subscription ของคุณใกล้หมดอายุ",
      message: "เรียนคุณครู,\n\nSubscription ของคุณจะหมดอายุในอีก 7 วัน กรุณาต่ออายุเพื่อใช้งานต่อไป\n\nขอบคุณครับ"
    },
    {
      id: "new_feature",
      name: "ฟีเจอร์ใหม่",
      subject: "ฟีเจอร์ใหม่! มาดูกันเลย",
      message: "สวัสดีครับ!\n\nเรามีฟีเจอร์ใหม่ที่น่าสนใจมาแนะนำ เข้ามาดูได้เลยที่ระบบ\n\nทีมงาน EduMi"
    },
    {
      id: "maintenance",
      name: "แจ้งปรับปรุงระบบ",
      subject: "แจ้งปรับปรุงระบบ",
      message: "เรียนผู้ใช้งานทุกท่าน,\n\nระบบจะปรับปรุงในวันที่ [DATE] เวลา [TIME] - [TIME]\n\nขออภัยในความไม่สะดวก\nทีมงาน EduMi"
    }
  ];

  useEffect(() => {
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      const usersSnap = await getDocs(collection(db, "users"));
      const usersData = usersSnap.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const handleSendNotification = async () => {
    if (!subject.trim() || !message.trim()) {
      setStatusMessage("กรุณากรอกหัวข้อและข้อความ");
      setSendStatus("error");
      return;
    }

    setLoading(true);
    setSendStatus("sending");
    setStatusMessage("กำลังส่งการแจ้งเตือน...");

    try {
      let targetUsers: any[] = [];

      if (targetType === "all") {
        targetUsers = users;
      } else if (targetType === "instructors") {
        targetUsers = users.filter(u => u.role === "instructor" || u.role === "admin");
      } else if (targetType === "students") {
        targetUsers = users.filter(u => u.role === "student");
      } else if (targetType === "specific") {
        targetUsers = users.filter(u => selectedUsers.includes(u.uid));
      }

      // Create notifications in Firestore
      const notificationsRef = collection(db, "notifications");
      const promises = targetUsers.map(targetUser =>
        addDoc(notificationsRef, {
          userId: targetUser.uid,
          userEmail: targetUser.email,
          subject,
          message,
          read: false,
          createdAt: serverTimestamp(),
          sentBy: user?.email || "admin"
        })
      );

      await Promise.all(promises);

      setSendStatus("success");
      setStatusMessage(`ส่งการแจ้งเตือนสำเร็จไปยัง ${targetUsers.length} คน`);
      
      // Reset form
      setSubject("");
      setMessage("");
      setSelectedUsers([]);
      setTargetType("all");

      setTimeout(() => {
        setSendStatus("idle");
        setStatusMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error sending notifications:", error);
      setSendStatus("error");
      setStatusMessage("เกิดข้อผิดพลาดในการส่งการแจ้งเตือน");
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template: NotificationTemplate) => {
    setSubject(template.subject);
    setMessage(template.message);
  };

  const filteredUsers = users.filter(u => {
    if (targetType === "instructors") return u.role === "instructor" || u.role === "admin";
    if (targetType === "students") return u.role === "student";
    return true;
  });

  const recipientCount = targetType === "specific" ? selectedUsers.length : filteredUsers.length;

  return (
    <div className="min-h-screen bg-gradient-soft py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-textDark flex items-center gap-3">
                  <Bell className="w-10 h-10 text-pink-600" />
                  การแจ้งเตือน
                </h1>
                <p className="text-textLight">ส่งข้อความและการแจ้งเตือนถึงผู้ใช้</p>
              </div>
            </div>
            <Link to="/">
              <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                <Home className="w-5 h-5" />
                <span>ไปหน้าแรก</span>
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">ผู้ใช้ทั้งหมด</p>
                  <p className="text-3xl font-bold text-textDark">{users.length}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">ผู้สอน</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {users.filter(u => u.role === "instructor" || u.role === "admin").length}
                  </p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">ผู้เรียน</p>
                  <p className="text-3xl font-bold text-green-600">
                    {users.filter(u => u.role === "student").length}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Templates Sidebar */}
          <Card className="md:col-span-1">
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-blue-600" />
                เทมเพลต
              </h3>
              <div className="space-y-3">
                {templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    className="w-full text-left p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl hover:shadow-lg transition-all border-2 border-transparent hover:border-blue-200"
                  >
                    <p className="font-semibold text-textDark mb-1">{template.name}</p>
                    <p className="text-xs text-textLight line-clamp-2">{template.subject}</p>
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* Main Form */}
          <Card className="md:col-span-2">
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-6 flex items-center gap-2">
                <Send className="w-6 h-6 text-pink-600" />
                ส่งการแจ้งเตือน
              </h3>

              <div className="space-y-6">
                {/* Target Type */}
                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    ส่งถึง *
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { value: "all", label: "ทุกคน", icon: Users },
                      { value: "instructors", label: "ผู้สอน", icon: User },
                      { value: "students", label: "ผู้เรียน", icon: Users },
                      { value: "specific", label: "เลือกเอง", icon: Mail }
                    ].map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        onClick={() => setTargetType(value as any)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl font-semibold transition-all ${
                          targetType === value
                            ? "bg-gradient-primary text-white shadow-lg"
                            : "bg-gray-100 text-textDark hover:bg-gray-200"
                        }`}
                      >
                        <Icon className="w-6 h-6" />
                        <span className="text-sm">{label}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-textLight mt-2">
                    จำนวนผู้รับ: <strong>{recipientCount}</strong> คน
                  </p>
                </div>

                {/* Specific Users Selection */}
                {targetType === "specific" && (
                  <div>
                    <label className="block text-sm font-semibold text-textDark mb-2">
                      เลือกผู้ใช้
                    </label>
                    <div className="max-h-48 overflow-y-auto border-2 border-gray-200 rounded-xl p-4 space-y-2">
                      {users.map(u => (
                        <label key={u.uid} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(u.uid)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers([...selectedUsers, u.uid]);
                              } else {
                                setSelectedUsers(selectedUsers.filter(id => id !== u.uid));
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <div>
                            <p className="text-sm font-semibold text-textDark">{u.name || "ไม่ระบุ"}</p>
                            <p className="text-xs text-textLight">{u.email}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subject */}
                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    หัวข้อ *
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="หัวข้อการแจ้งเตือน"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    ข้อความ *
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="เขียนข้อความที่ต้องการส่ง..."
                    rows={8}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none resize-none"
                  />
                  <p className="text-xs text-textLight mt-1">
                    {message.length} ตัวอักษร
                  </p>
                </div>

                {/* Status Message */}
                {sendStatus !== "idle" && (
                  <div className={`p-4 rounded-xl flex items-center gap-3 ${
                    sendStatus === "success" ? "bg-green-50 border-2 border-green-200" :
                    sendStatus === "error" ? "bg-red-50 border-2 border-red-200" :
                    "bg-blue-50 border-2 border-blue-200"
                  }`}>
                    {sendStatus === "success" ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : sendStatus === "error" ? (
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    ) : (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    )}
                    <p className={`font-semibold ${
                      sendStatus === "success" ? "text-green-700" :
                      sendStatus === "error" ? "text-red-700" :
                      "text-blue-700"
                    }`}>
                      {statusMessage}
                    </p>
                  </div>
                )}

                {/* Send Button */}
                <button
                  onClick={handleSendNotification}
                  disabled={loading || !subject.trim() || !message.trim() || recipientCount === 0}
                  className="w-full bg-gradient-primary text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      กำลังส่ง...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      ส่งการแจ้งเตือน ({recipientCount} คน)
                    </>
                  )}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
